// Made-to-order products size the engraving through the variant the customer picks, so the parsing
// of that variant is shared between the MTO components rather than duplicated in each of them.

// The option a made-to-order product uses to carry the engraving length
export const CHAR_COUNT_OPTION_NAME = "Number of Characters";

/**
 * The range of characters a variant covers. Its title holds either a range ("1-3", "4-6", ...) or
 * a single number ("3", which covers exactly 3 characters).
 * Returns `null` when the variant carries no number at all (a product with a single variant
 * titled "Default Title", for instance), leaving the caller to pick its own fallback.
 */
export function getVariantCharRange(variant) {
  const rawValue =
    variant?.selectedOptions?.find((option) => option.name === CHAR_COUNT_OPTION_NAME)?.value ??
    variant?.title ??
    "";

  const numbers = String(rawValue).match(/\d+/g);
  if (!numbers?.length) return null;

  const values = numbers.map(Number);
  return { min: Math.min(...values), max: Math.max(...values) };
}

// A variant sizing the engraving spells the range out in its title ("4-6"). Variants carrying a
// single number are only read as a character count when the option names them as one, as a lone
// number is a size on every other product ("6" on a ring).
const CHAR_COUNT_RANGE_TITLE = /^\s*\d+\s*-\s*\d+\s*$/;

/**
 * The range of characters a variant allows, but only for the variants actually sizing the
 * engraving. Everything else (a ring size, the "Yes"/"No" of a paid back engraving) returns `null`,
 * so a product whose variants have nothing to do with the engraving is never held back by one.
 */
export function getEngravingCharRange(variant) {
  const hasCharCountOption = (variant?.selectedOptions ?? []).some(
    (option) => option?.name === CHAR_COUNT_OPTION_NAME
  );
  if (!hasCharCountOption && !CHAR_COUNT_RANGE_TITLE.test(variant?.title ?? "")) return null;

  return getVariantCharRange(variant);
}

/**
 * How many characters the selected variant allows: the upper bound of its range is the maximum
 * that can be engraved. Returns `null` when the variant carries no number at all.
 */
export function getVariantCharCount(selectedVariant) {
  return getVariantCharRange(selectedVariant)?.max ?? null;
}

export default getVariantCharCount;
